/**
 * Company chat (owner, 20 Oct 2026: "a chatbox sort of thing between employee
 * company conversation with emoji support and cute animations").
 *
 * He chose: one company room and private 1-to-1 rooms; employees only, the
 * ASMs included, drivers not (they are not full-time staff — the same line as
 * attendance), never a mitr's, outlet's or dealer's counter; text, emoji,
 * reactions and photos. The owner's head-admin sign-ins are in it too — it is
 * the company's conversation, and he is the company.
 *
 * The chat belongs to no book: whichever book a person's header is on, it is
 * the same company room and the same private rooms.
 */
import { isHeadAdminDesk, isVerifiedEmployee, type PersonRuleInput } from './domain.js';

/** Who is in the chat: every employee, and the owner's desks. */
export function chatsInCompany(user: PersonRuleInput | null | undefined): boolean {
  if (!user) return false;
  if (isHeadAdminDesk(user)) return true;
  return isVerifiedEmployee(user);
}

export const CHAT_ROOM_KINDS = ['COMPANY', 'DIRECT'] as const;
export type ChatRoomKind = (typeof CHAT_ROOM_KINDS)[number];

/** TEXT: words, emoji and photos. KUDOS: a shout-out to one colleague, in the company room only. */
export const CHAT_MESSAGE_KINDS = ['TEXT', 'KUDOS'] as const;
export type ChatMessageKind = (typeof CHAT_MESSAGE_KINDS)[number];

export const CHAT_COMPANY_KEY = 'company';
export const CHAT_COMPANY_TITLE = 'Company room';

/** The private room of two people: their ids, sorted, joined — one room per pair, whoever opens it first. */
export function chatDirectKey(a: string, b: string): string {
  return [a, b].sort().join(':');
}

export const CHAT_TEXT_MAX = 2000;
export const CHAT_PHOTOS_MAX = 6;
/** How many messages a page holds, newest last. */
export const CHAT_PAGE_SIZE = 40;
/** Sends per person per minute before the server says slow down. */
export const CHAT_SENDS_PER_MINUTE = 30;
/** How long "is typing…" lasts after the last keystroke was reported. */
export const CHAT_TYPING_MS = 6_000;

/** The reactions offered at one tap; any other emoji comes from the picker. */
export const CHAT_QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🎉', '🙏'] as const;

/**
 * One emoji (a flag, a skin tone and a family are each one): what a reaction
 * may be. Short and pictographic — never words dressed as an emoji.
 */
export function isChatEmoji(value: string): boolean {
  const text = value.trim();
  if (text.length === 0 || text.length > 16 || /[\p{L}\s]/u.test(text)) return false;
  // A keycap (1️⃣) is a digit with its mark; a flag is two regional letters.
  return /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|\u20E3/u.test(text);
}

/** A person as the chat shows them. */
export interface ChatPerson {
  id: string;
  name: string;
  /** The role in the owner's words: "ASM", "Billing", "Service desk"… */
  role: string;
  /** The book they sit in, short: "Autozone", "BOW", "TVS", "FAB EV". */
  book: string | null;
}

export interface ChatPhotoView {
  id: string;
  mime: string;
  width: number | null;
  height: number | null;
}

export interface ChatReactionView {
  emoji: string;
  count: number;
  /** Whether the viewer is one of them — the chip shows pressed. */
  mine: boolean;
  /** Up to ten names, for the tooltip. */
  people: string[];
}

export interface ChatMessageView {
  id: string;
  roomId: string;
  kind: ChatMessageKind;
  author: ChatPerson | null;
  /** Null once removed: a removed message keeps its place and says so. */
  body: string | null;
  photos: ChatPhotoView[];
  reactions: ChatReactionView[];
  replyTo: { id: string; author: string | null; preview: string } | null;
  kudosTo: ChatPerson | null;
  clientId: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  mine: boolean;
  /** The viewer may remove it: their own, or any in the company room for the owner's desks. */
  canDelete: boolean;
  canEdit: boolean;
}

export interface ChatRoomView {
  id: string;
  kind: ChatRoomKind;
  /** "Company room", or the other person's name. */
  title: string;
  /** DIRECT: the other person. */
  person: ChatPerson | null;
  lastMessage: { id: string; at: string; author: string | null; preview: string } | null;
  unread: number;
  lastMessageAt: string | null;
}

export interface ChatInbox {
  /** The company room first, then the private rooms with the newest message first. */
  rooms: ChatRoomView[];
  unreadTotal: number;
}

/**
 * A page of one room. Opening a room (or scrolling up, `before`) returns
 * messages oldest→newest; a poll (`since`, the last `cursor`) returns every
 * message new OR changed since then — an edit, a removal, a reaction — and the
 * screen merges them by id.
 */
export interface ChatMessagesPage {
  room: ChatRoomView;
  data: ChatMessageView[];
  /** There are older messages than the first one here. */
  hasMore: boolean;
  /** Hand back as `since` on the next poll. */
  cursor: string;
  /** Names of the others typing in this room right now. */
  typing: string[];
  /** DIRECT: when the other person last read the room — "Seen" under one's own last message. */
  otherReadAt: string | null;
}

export interface ChatSendInput {
  body?: string | null;
  photoIds?: string[];
  replyToId?: string | null;
  /** A shout-out: the colleague it is for (company room only). */
  kudosToId?: string | null;
  /** The screen's own id for this message, so a retried send never posts twice. */
  clientId: string;
}
